import React, { useCallback, useEffect, useState } from "react";

import { HeartHandshake, Loader2, Save } from "lucide-react";

import Button from "../../../Components/Button";
import { useAppDispatch, useAppSelector } from "../../../Store/Hooks";
import type { LowScoreCriteria } from "../../../Store/Reducers/MyPortfolioOverviewSlice";
import {
  fetchLowScoreCriteriaConfig,
  updateLowScoreCriteriaConfig,
} from "../../../Store/Reducers/VendorAdminSlice";
import { toast } from "../../../Utils/Helper/toast";
import {
  DEFAULT_LOW_SCORE_CRITERIA,
  LOW_SCORE_CRITERIA_KEY,
  normalizeLowScoreCriteria,
} from "../../../Utils/LowScoreCriteria";
import LowScoreCriteriaForm from "../LowScoreCriteriaForm";

const LowScoreCriteriaOrgSettings: React.FC = () => {
  const dispatch = useAppDispatch();
  const {
    lowScoreCriteriaValue,
    lowScoreCriteriaLoading,
    lowScoreCriteriaUpdateLoading,
  } = useAppSelector(state => state.vendorAdmin);

  const [settings, setSettings] = useState<LowScoreCriteria>(
    DEFAULT_LOW_SCORE_CRITERIA
  );

  const load = useCallback(async () => {
    try {
      const value = await dispatch(fetchLowScoreCriteriaConfig()).unwrap();
      setSettings(normalizeLowScoreCriteria(value));
    } catch {
      setSettings(DEFAULT_LOW_SCORE_CRITERIA);
    }
  }, [dispatch]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    if (lowScoreCriteriaValue) {
      setSettings(normalizeLowScoreCriteria(lowScoreCriteriaValue));
    }
  }, [lowScoreCriteriaValue]);

  const handleSave = async () => {
    const normalized = normalizeLowScoreCriteria(settings);
    try {
      const value = await dispatch(
        updateLowScoreCriteriaConfig({
          key: LOW_SCORE_CRITERIA_KEY,
          value: normalized,
        })
      ).unwrap();
      setSettings(normalizeLowScoreCriteria(value));
      toast.success("Low Score criteria saved");
    } catch (error: unknown) {
      toast.error(
        typeof error === "string"
          ? error
          : "Failed to save Low Score criteria settings"
      );
    }
  };

  const handleReset = () => {
    setSettings(DEFAULT_LOW_SCORE_CRITERIA);
  };

  return (
    <div className="settings-section rounded-lg border border-gray-200 bg-white shadow">
      <div className="p-6">
        <div className="mb-4 flex items-center gap-2">
          <HeartHandshake className="h-5 w-5 text-purple-600" />
          <h2 className="fs-18 fw-600 mb-0 text-gray-900">
            Portfolio — Relationship Low Score
          </h2>
        </div>
        <p className="mb-4 text-sm text-gray-600">
          Relationship pairs matching <strong>any</strong> of the active
          criteria below are flagged as Low Score on the Relationship Priorities
          tab.
        </p>

        {lowScoreCriteriaLoading ? (
          <div className="flex h-10 items-center gap-2 text-sm text-gray-500">
            <Loader2 className="h-4 w-4 animate-spin text-violet-600" />
            Loading…
          </div>
        ) : (
          <div className="space-y-5">
            <LowScoreCriteriaForm
              value={settings}
              onChange={patch => setSettings(prev => ({ ...prev, ...patch }))}
            />

            <div className="flex justify-end gap-2">
              <Button
                variant="secondary"
                onClick={handleReset}
                disabled={lowScoreCriteriaUpdateLoading}
              >
                Reset to defaults
              </Button>
              <Button
                variant="primary"
                onClick={handleSave}
                disabled={lowScoreCriteriaUpdateLoading}
                loading={lowScoreCriteriaUpdateLoading}
                icon={<Save className="h-4 w-4" />}
              >
                Save
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default LowScoreCriteriaOrgSettings;
